import type { CoverStore } from './gymCover';
import {
  memberPhotoMime,
  memberPhotoUploadsAvailable,
  normalizeMemberPhoto,
  type PhotoUploadProgressHandler,
  uploadBinaryWithProgress,
} from './memberMediaUpload';
import { beginPhotoUpload, endPhotoUpload, updatePhotoUpload } from './photoUploadProgress';
import { coalesceImageDelivery } from './imageDelivery';
import { avatarCropRect, type AvatarCrop } from './avatarCrop';

const PREFIX = 'ggb.avatar.';
export const MAX_AVATAR_BYTES = 512 * 1024;
const MAX_INPUT_BYTES = 40 * 1024 * 1024;
const listeners = new Set<() => void>();
export function subscribeAvatars(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function refreshAvatars() {
  for (const listener of listeners) listener();
}
function avatarKey(userId: string) {
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(userId)) throw new Error('That avatar could not be saved.');
  return PREFIX + userId;
}
export function avatarImageSource(value?: string | null): string | null {
  if (!value) return null;
  if (/^\/images\/[A-Za-z0-9_./-]+\.(?:png|jpe?g|webp)$/i.test(value) && !value.includes('..'))
    return value;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}
export type RemoteAvatarImage = {
  url: string;
  userId: string;
  mediaId: string;
  token: string | undefined;
  revision: number;
};

/** A selected shared photo supersedes older URLs, including while delivery is pending. */
export function resolveAvatarImage(input: {
  live: boolean;
  userId?: string;
  mediaId?: string;
  token?: string;
  revision: number;
  source?: string;
  remote: RemoteAvatarImage | null;
  /** Staff lists may probe delivery before avatar_media_id is known. */
  probe?: boolean;
}): string | null {
  if (!input.live) return avatarImageSource(input.source);
  const expectedMediaId = input.mediaId || (input.probe ? '' : null);
  if (expectedMediaId === null) return avatarImageSource(input.source);
  const remote = input.remote;
  return remote?.userId === input.userId &&
    remote?.mediaId === expectedMediaId &&
    remote?.token === input.token &&
    remote?.revision === input.revision
    ? remote.url
    : null;
}

/** Existing-avatar delivery is independent from whether the server accepts new uploads. */
export async function loadMemberAvatar(
  apiBase: string,
  userId: string,
  token: string | undefined,
  signal: AbortSignal,
  fetcher: typeof fetch = fetch,
  deliveryVersion = '',
): Promise<Blob | null> {
  if (signal.aborted || !/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(userId)) return null;
  return coalesceImageDelivery(
    fetcher,
    JSON.stringify([apiBase, 'avatar', userId, token ?? null, deliveryVersion]),
    signal,
    async (sharedSignal) => {
      const response = await fetcher(`${apiBase}/profiles/${userId}/avatar`, {
        signal: sharedSignal,
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (
        sharedSignal.aborted ||
        !response.ok ||
        !/^image\/(jpeg|png|webp)(?:;|$)/i.test(response.headers.get('Content-Type') ?? '') ||
        Number(response.headers.get('Content-Length') ?? 0) > MAX_AVATAR_BYTES
      )
        return null;
      const blob = await response.blob();
      return !sharedSignal.aborted && blob.size > 0 && blob.size <= MAX_AVATAR_BYTES ? blob : null;
    },
  );
}
export function avatarFileError(file: { type: string; size: number }) {
  const mime = memberPhotoMime(file.type);
  if (mime && !['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(mime))
    return 'Use a JPEG, PNG, or WebP image.';
  if (file.size <= 0) return 'Choose an image under 8 MB.';
  if (file.size > MAX_INPUT_BYTES) return 'That camera photo is too large. Try a smaller picture.';
  return null;
}
export function clearAvatar(userId: string, store?: CoverStore) {
  try {
    (store ?? localStorage).removeItem(avatarKey(userId));
  } catch {
    // A retired local photo cannot block shared uploads or removal.
  }
  refreshAvatars();
}
/** Remove retired browser-only photos without reading or uploading their contents. */
export function clearAvatars(store?: CoverStore) {
  try {
    const storage = store ?? localStorage;
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Browser storage may be unavailable; shared avatars do not depend on it.
  }
  refreshAvatars();
}
/** Decode, crop and re-encode to strip original metadata and bound the avatar size. */
export async function prepareAvatar(file: Blob, selection?: AvatarCrop): Promise<{ blob: Blob }> {
  const error = avatarFileError(file);
  if (error) throw new Error(error);
  // Camera captures are often >8 MB; normalize first so decode stays bounded.
  const prepared = await normalizeMemberPhoto(file);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(prepared);
  } catch {
    throw new Error('That image could not be opened.');
  }
  try {
    if (bitmap.width < 1 || bitmap.height < 1 || bitmap.width * bitmap.height > 20_000_000)
      throw new Error('Use a photo under 20 million pixels.');
    const crop = avatarCropRect(bitmap.width, bitmap.height, selection);
    const edge = Math.max(1, Math.floor(Math.min(crop.side, 512)));
    const canvas = document.createElement('canvas');
    canvas.width = edge;
    canvas.height = edge;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser could not prepare that image.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, edge, edge);
    context.drawImage(bitmap, crop.x, crop.y, crop.side, crop.side, 0, 0, edge, edge);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.86),
    );
    if (!blob || blob.size < 1 || blob.size > MAX_AVATAR_BYTES)
      throw new Error('That image could not be prepared. Try a smaller photo.');
    return { blob };
  } finally {
    bitmap.close();
  }
}

type Fetcher = typeof fetch;
async function checkedJson(response: Response) {
  const body = await response.json();
  if (!response.ok) throw new Error(body.error?.message ?? 'The avatar could not be saved.');
  return body.data;
}
export async function avatarUploadsAvailable(apiBase: string, fetcher: Fetcher = fetch) {
  return memberPhotoUploadsAvailable(apiBase, fetcher);
}
export async function uploadAvatar(
  apiBase: string,
  token: string,
  userId: string,
  blob: Blob,
  fetcher: Fetcher = fetch,
  onProgress?: PhotoUploadProgressHandler,
) {
  if (!token) throw new Error('Sign in to upload an avatar.');
  if (blob.type !== 'image/jpeg' || blob.size < 1 || blob.size > MAX_AVATAR_BYTES)
    throw new Error('Prepare a JPEG avatar under 512 KB before uploading.');
  const progressId = `avatar-${Date.now()}`;
  beginPhotoUpload(progressId, 'Uploading avatar…', 5);
  try {
    onProgress?.({ phase: 'reserving', label: 'Reserving upload…', percent: 15 });
    updatePhotoUpload(progressId, 'Reserving upload…', 15);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const reservation = await checkedJson(
      await fetcher(`${apiBase}/media/reservations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          kind: 'photo',
          contentType: blob.type,
          byteSize: blob.size,
          targetType: 'profile',
          targetId: userId,
        }),
      }),
    );
    onProgress?.({
      phase: 'uploading',
      label: 'Uploading avatar…',
      percent: 25,
      loaded: 0,
      total: blob.size,
    });
    updatePhotoUpload(progressId, 'Uploading avatar…', 25);
    const uploadResponse = await uploadBinaryWithProgress(
      `${apiBase}/media/uploads/${encodeURIComponent(reservation.reservationId)}`,
      {
        token,
        blob,
        contentType: blob.type,
        fetcher,
        onProgress: (loaded, total) => {
          const percent = 25 + Math.round((total > 0 ? loaded / total : 0) * 65);
          onProgress?.({
            phase: 'uploading',
            label: 'Uploading avatar…',
            percent,
            loaded,
            total,
          });
          updatePhotoUpload(progressId, 'Uploading avatar…', percent);
        },
      },
    );
    const uploadBody = await uploadResponse.json();
    if (!uploadResponse.ok)
      throw new Error(uploadBody.error?.message ?? 'The avatar could not be saved.');
    const uploaded = uploadBody.data;
    onProgress?.({ phase: 'processing', label: 'Finishing avatar…', percent: 95 });
    updatePhotoUpload(progressId, 'Finishing avatar…', 95);
    if (uploaded.status !== 'ready')
      throw new Error(
        'Your avatar is still being processed. Try again when processing is available.',
      );
    await checkedJson(
      await fetcher(`${apiBase}/profiles/me/avatar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mediaId: uploaded.mediaId }),
      }),
    );
    onProgress?.({ phase: 'processing', label: 'Avatar ready', percent: 100 });
    updatePhotoUpload(progressId, 'Avatar ready', 100);
    refreshAvatars();
  } finally {
    endPhotoUpload(progressId);
  }
}
export async function removeUploadedAvatar(
  apiBase: string,
  token: string,
  fetcher: Fetcher = fetch,
) {
  if (!token) throw new Error('Sign in to remove an avatar.');
  await checkedJson(
    await fetcher(`${apiBase}/profiles/me/avatar`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
  refreshAvatars();
}
